import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Linkedin, Youtube, AtSign, Mail, Phone, MapPin, Send } from "lucide-react";
import { CONTACT, api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const QUICK = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/products", label: "Products" },
  { to: "/bulk-orders", label: "Bulk Orders" },
  { to: "/contact", label: "Contact" },
];
const MORE = [
  { to: "/become-distributor", label: "Become a Distributor" },
  { to: "/wholesale-pricing", label: "Wholesale Pricing" },
  { to: "/free-sample", label: "Request Free Sample" },
  { to: "/faq", label: "FAQ" },
  { to: "/blog", label: "Blog" },
];
const LEGAL = [
  { to: "/privacy-policy", label: "Privacy Policy" },
  { to: "/cookie-policy", label: "Cookie Policy" },
  { to: "/terms", label: "Terms & Conditions" },
  { to: "/refund-shipping", label: "Refund & Shipping" },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    if (!privacyConsent) {
      toast.error("Please agree to the Privacy Policy before subscribing.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/leads", {
        type: "newsletter",
        email,
        privacy_consent: true,
        privacy_consent_at: new Date().toISOString(),
        privacy_policy_version: "1.0",
        marketing_consent: marketingConsent,
        marketing_consent_at: marketingConsent ? new Date().toISOString() : null,
      });
      toast.success("Subscribed! Welcome to the Leaf Alchemy family.");
      setEmail("");
      setPrivacyConsent(false);
      setMarketingConsent(false);
    } catch {
      toast.error("Subscription failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const socials = [
    { icon: Instagram, href: CONTACT.socials.instagram, name: "instagram" },
    { icon: Facebook, href: CONTACT.socials.facebook, name: "facebook" },
    { icon: AtSign, href: CONTACT.socials.threads, name: "threads" },
    { icon: Linkedin, href: CONTACT.socials.linkedin, name: "linkedin" },
    { icon: Youtube, href: CONTACT.socials.youtube, name: "youtube" },
  ];

  return (
    <footer className="bg-forest text-cream relative overflow-hidden" data-testid="footer">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 grid gap-12 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <img src="/brand/logo-cream.png" alt="Leaf Alchemy" className="h-24 w-auto object-contain mb-4" />
          <p className="text-cream/70 text-sm leading-relaxed max-w-xs">
            Premium tea powder crafted for hotels, restaurants, tea outlets and tea lovers across India.
          </p>
          <div className="flex gap-3 mt-6">
            {socials.map((s) => (
              <a
                key={s.name}
                href={s.href}
                target="_blank"
                rel="noreferrer"
                data-testid={`footer-social-${s.name}`}
                className="w-10 h-10 rounded-full border border-cream/20 grid place-items-center hover:bg-gold hover:text-ink hover:border-gold transition-colors"
              >
                <s.icon size={17} />
              </a>
            ))}
          </div>
        </div>

        <div>
          <h4 className="font-display text-lg text-gold mb-4">Quick Links</h4>
          <ul className="space-y-2.5">
            {QUICK.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-cream/75 text-sm hover:text-gold transition-colors">{l.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-display text-lg text-gold mb-4">Explore</h4>
          <ul className="space-y-2.5">
            {MORE.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-cream/75 text-sm hover:text-gold transition-colors">{l.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-display text-lg text-gold mb-4">Get in Touch</h4>
          <ul className="space-y-3 text-sm text-cream/80">
            <li className="flex items-center gap-2"><Phone size={15} className="text-gold" /><a href={`tel:${CONTACT.phoneRaw}`} className="hover:text-gold">{CONTACT.phone}</a></li>
            <li className="flex items-center gap-2"><Mail size={15} className="text-gold" /><a href={`mailto:${CONTACT.email}`} className="hover:text-gold break-all">{CONTACT.email}</a></li>
            <li className="flex items-start gap-2"><MapPin size={15} className="text-gold mt-0.5" /><a href={CONTACT.location.mapsUrl} target="_blank" rel="noreferrer" className="hover:text-gold break-words">{CONTACT.location.address}</a></li>
          </ul>
          <form onSubmit={subscribe} className="mt-5 space-y-3" data-testid="newsletter-form">
            <div className="flex gap-2">
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                data-testid="newsletter-email"
                className="bg-cream/10 border-cream/20 text-cream placeholder:text-cream/50 focus-visible:ring-gold"
              />
              <Button type="submit" disabled={busy} data-testid="newsletter-submit" className="rounded-full bg-gold text-ink hover:bg-cream shrink-0 px-4">
                <Send size={16} />
              </Button>
            </div>
            <label className="flex items-start gap-2 text-xs text-cream/80">
              <input type="checkbox" checked={privacyConsent} onChange={(e) => setPrivacyConsent(e.target.checked)} className="mt-0.5 accent-gold" required />
              <span>I agree to the <a href="/privacy-policy" className="underline">Privacy Policy</a> for this newsletter request.</span>
            </label>
            <label className="flex items-start gap-2 text-xs text-cream/80">
              <input type="checkbox" checked={marketingConsent} onChange={(e) => setMarketingConsent(e.target.checked)} className="mt-0.5 accent-gold" />
              <span>I would like to receive Leaf Alchemy product updates, offers and newsletters.</span>
            </label>
          </form>
        </div>
      </div>

      <div className="border-t border-cream/15">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-cream/60">
          <p>© 2026 Leaf Alchemy. All Rights Reserved.</p>
          <div className="flex gap-4">
            {LEGAL.map((l) => (
              <Link key={l.to} to={l.to} className="hover:text-gold transition-colors">{l.label}</Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
